import { IMAGE_EXTENSIONS, VIDEO_EXTENSIONS } from '../../api/progressiveUploadService'
import { ImageBaseUrl } from '../../constants'

type DownloadFileOptions = {
  mediaType?: 'image' | 'video' | 'audio' | 'file' | 'document'
  fallbackFileName?: string
}

export const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

export const getFileIconId = (file: File) => {
  const type = file.type
  const extension = file.name.split('.').pop()?.toLowerCase() || ''
  if (type.startsWith('image/') || IMAGE_EXTENSIONS.includes(extension)) return 'file-image'
  if (type.startsWith('video/') || VIDEO_EXTENSIONS.includes(extension)) return 'file-video'
  if (type.startsWith('audio/') || type === 'application/pdf') return 'file-document'
  return 'file-generic'
}

export const getDocumentIconId = (fileType?: string, messageType?: string): string => {
  const type = fileType?.toLowerCase() || messageType?.toLowerCase() || ''

  if (type.includes('pdf')) return 'file-pdf'
  if (type.includes('doc') || type.includes('word')) return 'file-word'
  if (type.includes('xls') || type.includes('excel') || type.includes('sheet')) return 'file-excel'
  if (type.includes('image') || IMAGE_EXTENSIONS.some((extension) => type.includes(extension)))
    return 'file-image'
  if (type.includes('video') || VIDEO_EXTENSIONS.some((extension) => type.includes(extension)))
    return 'file-video'
  if (type.includes('audio') || type.includes('mp3')) return 'file-audio'
  if (type.includes('zip') || type.includes('rar')) return 'file-archive'
  if (type.includes('text') || type.includes('txt')) return 'file-text'

  return 'file-generic'
}

export const getDocumentColorClass = (fileType?: string, messageType?: string): string => {
  const type = fileType?.toLowerCase() || messageType?.toLowerCase() || ''

  if (type.includes('pdf')) return 'bg-light-danger'
  if (type.includes('doc') || type.includes('word')) return 'bg-light-primary'
  if (type.includes('xls') || type.includes('excel') || type.includes('sheet')) return 'bg-light-success'
  if (type.includes('image')) return 'bg-light-warning'
  if (type.includes('video')) return 'bg-light-info'
  if (type.includes('audio')) return 'bg-light-primary'

  return 'bg-light'
}

const isAbsoluteDownloadUrl = (url: string) => /^(https?:|data:|blob:|\/\/)/i.test(url)

export const resolveDownloadUrl = (fileUrl: string): string => {
  const normalizedUrl = String(fileUrl || '').trim()

  if (!normalizedUrl) return ''

  if (isAbsoluteDownloadUrl(normalizedUrl)) {
    return normalizedUrl
  }

  const baseUrl = String(ImageBaseUrl || '').trim()

  if (!baseUrl) {
    return normalizedUrl
  }

  return `${baseUrl.replace(/\/$/, '')}/${normalizedUrl.replace(/^\//, '')}`
}

export const getDownloadFileName = (fileUrl: string, fallbackFileName = 'download'): string => {
  try {
    const resolvedUrl = resolveDownloadUrl(fileUrl)
    const parsedUrl = new URL(resolvedUrl, window.location.origin)
    const fileName = parsedUrl.pathname.split('/').pop()

    if (fileName && fileName !== 'original' && fileName !== 'playlist.m3u8') {
      return decodeURIComponent(fileName)
    }
  } catch {
    // Fall through to the provided fallback file name.
  }

  return fallbackFileName
}

export const buildVideoDownloadCandidates = (videoUrl: string): string[] => {
  const resolvedUrl = resolveDownloadUrl(videoUrl)
  const candidates: string[] = []

  const addCandidate = (candidate: string) => {
    if (candidate && !candidates.includes(candidate)) {
      candidates.push(candidate)
    }
  }

  try {
    const parsedUrl = new URL(resolvedUrl, window.location.origin)
    const currentPath = parsedUrl.pathname
    const query = parsedUrl.search

    if (/\/playlist\.m3u8$/i.test(currentPath)) {
      const resolutions = [1080, 720, 480, 360, 240]

      resolutions.forEach((resolution) => {
        addCandidate(
          `${parsedUrl.origin}${currentPath.replace(/\/playlist\.m3u8$/i, `/play_${resolution}p.mp4`)}${query}`,
        )
      })
      addCandidate(`${parsedUrl.origin}${currentPath.replace(/\/playlist\.m3u8$/i, '/original')}${query}`)
    }

    addCandidate(parsedUrl.toString())
  } catch {
    addCandidate(resolvedUrl)
  }

  return candidates
}

const triggerBrowserDownload = (url: string, fileName: string) => {
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.rel = 'noopener noreferrer'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

//file download
export const downloadFile = async (
  fileUrl: string,
  fileName?: string,
  options: DownloadFileOptions = {},
): Promise<void> => {
  if (!fileUrl) {
    console.warn('File URL not available')
    return
  }

  const fallbackFileName = options.fallbackFileName || fileName || 'download'
  const downloadCandidates =
    options.mediaType === 'video' || /\/playlist\.m3u8(?:$|\?)/i.test(fileUrl)
      ? buildVideoDownloadCandidates(fileUrl)
      : [resolveDownloadUrl(fileUrl)]

  try {
    for (const candidateUrl of downloadCandidates) {
      try {
        const response = await fetch(candidateUrl, {
          mode: 'cors',
        })

        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.statusText}`)
        }

        const blob = await response.blob()
        const blobUrl = URL.createObjectURL(blob)
        const resolvedFileName = fileName || getDownloadFileName(candidateUrl, fallbackFileName)

        triggerBrowserDownload(blobUrl, resolvedFileName)
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100)
        return
      } catch {
        // Try the next generated URL candidate.
      }
    }

    throw new Error('No download candidates succeeded')
  } catch (error) {
    console.error('Error downloading file:', error)
    if (typeof window !== 'undefined') {
      window.open(downloadCandidates[0], '_blank')
    }
  }
}

//file sharing
export const shareFile = async (fileUrl: string, fileName: string): Promise<boolean> => {
  if (navigator.share) {
    console.error('Share not Support')
    try {
      await navigator.share({
        title: fileName,
        url: fileUrl,
      })
      return true
    } catch (err) {
      return false
    }
  } else {
    try {
      await navigator.clipboard.writeText(fileUrl)
      console.error('Link copied to clipboard!')
      return true
    } catch (err) {
      console.error('Error copying to clipboard:', err)
      return false
    }
  }
}
